import { createAuthClient } from 'better-auth/react';

import { API_URL } from '../env';

/**
 * PRE-74: better-auth web client for the internal admin panel. Google OAuth,
 * plus email/password when the API opts in with EMAIL_PASSWORD_AUTH=true (BR-26).
 * Admin authorization itself is enforced by the API's `ADMIN_EMAILS` allowlist on
 * `/v1/admin/*` — this client only proves identity; a non-admin who signs in
 * (by either method) still gets 403 from the API.
 */
export const authClient = createAuthClient({
  baseURL: API_URL,
  basePath: '/api/auth',
});

type AdminSession = {
  user: { id: string; email: string; name?: string | null; image?: string | null };
  session: { id: string; token?: string; expiresAt: string | Date };
};

// Wrapped + explicitly typed so the exported types don't reference better-auth's
// internal .d.ts paths (TS2742 on `tsc -b`).
export function useSession(): { data: AdminSession | null; isPending: boolean } {
  const r = authClient.useSession() as { data: AdminSession | null; isPending: boolean };
  return { data: r.data, isPending: r.isPending };
}

export function signOut(): Promise<unknown> {
  return authClient.signOut();
}

/** Start the Google OAuth flow, returning to this app afterward. */
export function signInWithGoogle(): Promise<unknown> {
  return authClient.signIn.social({
    provider: 'google',
    callbackURL: window.location.origin,
  });
}

type AuthResult = { data: unknown; error: { message?: string; code?: string } | null };

/**
 * Email/password sign-in. Only works when the API has EMAIL_PASSWORD_AUTH=true
 * (BR-26 opt-in); otherwise better-auth returns an error. Identity only —
 * admin authorization is still the API's ADMIN_EMAILS allowlist.
 */
export async function signInWithEmail(email: string, password: string): Promise<void> {
  const { error } = (await authClient.signIn.email({ email, password })) as AuthResult;
  if (error) throw new Error(error.message || 'Sign-in failed');
}

/**
 * Bearer header from the current session token. Cross-origin cookies are
 * unreliable on web (the deployed worker runs in dev cookie mode), so admin API
 * calls authenticate with the session token — same approach as the mobile app.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  const { data } = (await authClient.getSession()) as { data: AdminSession | null };
  const token = data?.session?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}
